/**
 * useAccountGroups Hook
 * 
 * Fetches account groups in real-time from Firestore.
 * Handles default groups initialization if none exist.
 * Can be shared between Accounts.tsx and AccountSettings.tsx.
 */

import { useState, useEffect, useCallback } from "react";
import { auth } from "../../../firebase";
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  doc,
  setDoc,
  serverTimestamp,
  Timestamp,
  QuerySnapshot,
  DocumentSnapshot,
  Unsubscribe,
  FirestoreError,
} from "firebase/firestore";
import { db } from "../../../firebase";
import { AccountGroupDetailField } from "../../../types";
import { isDemoMode } from "../../../utils/demoMode";
import { isBypassAuthEnabled } from "../../../utils/bypassAuth";
import { DEFAULT_ACCOUNT_GROUPS } from "../../../config/accounts";

export interface AccountGroupDoc {
  id: string; // Group name (used as document ID)
  name: string;
  description?: string;
  order: number;
  createdAt: Timestamp | Date;
  details?: AccountGroupDetailField[];
}

const accountGroupsCache = new Map<string, { data: AccountGroupDoc[]; timestamp: number }>();
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 min, match accounts cache

/**
 * Hook to fetch account groups in real-time
 * @param workspaceId - Workspace ID
 * @returns Account groups array
 */
export function useAccountGroups(workspaceId: string | undefined): AccountGroupDoc[] {
  const cached = workspaceId ? accountGroupsCache.get(workspaceId) : null;
  const hasValidCache = !!(cached && (Date.now() - cached.timestamp) < CACHE_TTL_MS);

  const [accountGroups, setAccountGroups] = useState<AccountGroupDoc[]>(hasValidCache && cached ? cached.data : []);

  // Initialize default groups
  const initializeDefaultGroups = useCallback(async (): Promise<void> => {
    if (!workspaceId) return;
    try {
      // Use Promise.all for parallel writes (simpler than batch for initialization)
      await Promise.all(
        DEFAULT_ACCOUNT_GROUPS.map((group) => {
          const groupRef = doc(db, "workspaces", workspaceId, "accountGroups", group.name);
          return setDoc(groupRef, {
            name: group.name,
            order: group.order,
            createdAt: serverTimestamp(),
          });
        })
      );
    } catch (error) {
      console.error("Error initializing default groups:", error);
    }
  }, [workspaceId]);

  // Fetch account groups in real-time
  useEffect(() => {
    // Allow loading in demo mode or when authenticated
    if (!workspaceId || (!auth.currentUser && !isDemoMode() && !isBypassAuthEnabled())) {
      setAccountGroups([]);
      return;
    }

    const groupsRef = collection(db, "workspaces", workspaceId, "accountGroups");
    const q = query(groupsRef, orderBy("order", "asc"));

    const handleSnapshot = (snapshot: QuerySnapshot): void => {
      try {
        const groupsList: AccountGroupDoc[] = snapshot.docs.map((docSnap: DocumentSnapshot) => {
          const data = docSnap.data();
          if (!data) return null;
          return {
            id: docSnap.id,
            name: data.name as string,
            description: data.description as string | undefined,
            order: data.order as number,
            createdAt: data.createdAt || Timestamp.now(),
            details: (data.details as AccountGroupDetailField[] | undefined) || [],
          } as AccountGroupDoc;
        }).filter((g): g is AccountGroupDoc => g !== null);
        // Always sort by order (in case orderBy fails or for consistency)
        groupsList.sort((a, b) => a.order - b.order);
        
        // If no groups exist, initialize default groups
        if (groupsList.length === 0) {
          initializeDefaultGroups();
          return;
        }

        if (workspaceId) accountGroupsCache.set(workspaceId, { data: groupsList, timestamp: Date.now() });
        setAccountGroups(groupsList);
      } catch (error) {
        console.error("Error processing groups snapshot:", error);
        setAccountGroups([]);
      }
    };

    let unsubscribe: Unsubscribe | undefined;

    try {
      unsubscribe = onSnapshot(
        q,
        handleSnapshot,
        (error: FirestoreError) => {
          console.error("Error fetching account groups:", error);
          // Fallback query without orderBy if index is missing
          if (error.code === "failed-precondition" || error.message?.includes("index")) {
            const fallbackQ = query(groupsRef);
            unsubscribe = onSnapshot(
              fallbackQ,
              (fallbackSnapshot) => {
                const groupsList: AccountGroupDoc[] = fallbackSnapshot.docs.map((docSnap: DocumentSnapshot) => {
                  const data = docSnap.data();
                  if (!data) return null;
                  return {
                    id: docSnap.id,
                    name: data.name as string,
                    description: data.description as string | undefined,
                    order: data.order as number,
                    createdAt: data.createdAt || Timestamp.now(),
                    details: (data.details as AccountGroupDetailField[] | undefined) || [],
                  } as AccountGroupDoc;
                }).filter((g): g is AccountGroupDoc => g !== null);
                // Sort client-side if orderBy fails
                groupsList.sort((a, b) => a.order - b.order);
                
                // If no groups exist, initialize default groups
                if (groupsList.length === 0) {
                  initializeDefaultGroups();
                  return;
                }

                if (workspaceId) accountGroupsCache.set(workspaceId, { data: groupsList, timestamp: Date.now() });
                setAccountGroups(groupsList);
              },
              (fallbackError: FirestoreError) => {
                console.error("Fallback query also failed:", fallbackError);
                setAccountGroups([]);
              }
            );
          } else {
            setAccountGroups([]);
          }
        }
      );
    } catch (error) {
      console.error("Query setup error:", error);
      setAccountGroups([]);
    }

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [workspaceId, initializeDefaultGroups]);

  return accountGroups;
}
